import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
// Constants
import { HOME, MANAGE_AMA } from 'constants/routes';
// Types
import {
  RedeemTalentInviteLink,
  RedeemTalentInviteLinkVariables,
} from 'api/brandDealsSignUpInvite/types/RedeemTalentInviteLink';
import { UserRole } from 'api/graphql-global-types';
// Helpers
import {
  clearFullOnboardingLocalStorageFlag,
  clearSignUpInviteCodeFromLocalStorage,
  getSignUpInviteCodeFromLocalStorage,
  setFullOnboardingLocalStorageFlag,
  setRedirectToLocalStorage,
} from 'helpers/storage';
import { isStoreRole } from 'helpers/user';
// Hooks
import { useOnboardingContext } from 'hooks';
// Mutations
import { REEDEM_TALENT_INVITE_LINK } from 'api/brandDealsSignUpInvite/mutations';
// Components
import { showToast } from 'components/common/Toast/Toast';

export const useRedeemInvCode = () => {
  const { push } = useRouter();
  const [redeemTalentInviteLink, { loading }] = useMutation<
    RedeemTalentInviteLink,
    RedeemTalentInviteLinkVariables
  >(REEDEM_TALENT_INVITE_LINK);

  const { setFullOnboarding, setShowOnboardingModal } = useOnboardingContext();

  const clearFlags = useCallback(() => {
    clearSignUpInviteCodeFromLocalStorage();
    clearFullOnboardingLocalStorageFlag();
    setRedirectToLocalStorage(HOME);
  }, []);

  const tryToRedeemInviteCode = useCallback(
    async (
      isNewSignUp = true,
      userRole?: UserRole | null
    ): Promise<string | null> => {
      const signUpInviteCode = getSignUpInviteCodeFromLocalStorage();
      if (!signUpInviteCode) return null;

      // Non-store users on sign-in must finish onboarding before joining a campaign
      if (!isNewSignUp && !isStoreRole(userRole)) {
        setFullOnboarding(true);
        setFullOnboardingLocalStorageFlag('true');
        setShowOnboardingModal(true);
        return null;
      }

      try {
        const result = await redeemTalentInviteLink({
          variables: {
            input: {
              code: signUpInviteCode,
              isNewSignUp,
            },
          },
        });
        clearFlags();

        const offer = result.data?.redeemTalentInviteLink;
        const dest = offer?.campaign?.id
          ? `/manage-brand-deals/campaigns/${offer.campaign.id}`
          : MANAGE_AMA;

        // Existing sign-in users: redirect now, don't stay on the invite page.
        // New sign-up: the onboarding caller pushes `dest` after its own flow.
        if (!isNewSignUp) {
          push(dest);
        }
        return dest;
      } catch (error: any) {
        showToast({
          type: 'error',
          message:
            error?.message ||
            'Something went wrong while redeeming the invite code. Please try again.',
        });
        // Clear the invite code even on failure; athlete can retry from the invite page.
        clearFlags();
        return null;
      }
    },
    [
      redeemTalentInviteLink,
      setFullOnboarding,
      setShowOnboardingModal,
      push,
      clearFlags,
    ]
  );

  return { tryToRedeemInviteCode, loading };
};
